/**
 * Kartica »Projekti« na strani Uvoz (docs/ARHITEKTURA.md, 15): sifrant
 * projektov za delitev amortizacije in nastavitve modula. Samo skrbnik.
 *
 * Projekt ima naziv, stroskovni nosilec za vsak program, iz katerega podjetje
 * uvaza (po njem se knjizbe pripisejo projektu), predvideno amortizacijo
 * (razpolozljiva sredstva) in predviden zakljucek. Obrazec se odpre sele ob
 * kliku; isti obrazec sluzi dodajanju in spremembi.
 */
import {
  nazivVira,
  type NastavitveProjektov,
  type NosilecProjekta,
  type PregledProjektov,
  type Projekt,
  type VnosProjekta,
} from '@koda/shared';
import { useEffect, useState, type FormEvent, type JSX } from 'react';

import { api } from '../lib/api';
import { datum, znesekZValuto } from '../lib/format';

interface Lastnosti {
  podjetje: string;
}

interface Obrazec {
  id: string | null;
  naziv: string;
  nosilci: Record<string, string>;
  /** Znesek v slovenskem zapisu, kot ga tipka uporabnik. */
  predvidenaAmortizacija: string;
  zakljucek: string;
  aktiven: boolean;
  /** Zacetno stanje porabe v slovenskem zapisu in mesec (`llll-mm`), do katerega velja. */
  zacetnaPoraba: string;
  zacetnaPorabaDo: string;
}

const PRAZEN: Obrazec = { id: null, naziv: '', nosilci: {}, predvidenaAmortizacija: '', zakljucek: '', aktiven: true, zacetnaPoraba: '', zacetnaPorabaDo: '' };

/** Slovenski zapis zneska (120.000,00) v decimalno besedilo za API (120000.00); prazno = null. */
export function znesekZaApi(besedilo: string, kaj = 'Predvidena amortizacija'): string | null {
  const ociscen = besedilo.replace(/[\s.€]/g, '').replace(',', '.');
  if (ociscen === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(ociscen)) throw new Error(`${kaj} mora biti znesek, npr. 120.000,00.`);
  return Number(ociscen).toFixed(2);
}

function vVnos(o: Obrazec): VnosProjekta {
  const nosilci: NosilecProjekta[] = Object.entries(o.nosilci)
    .filter(([, nosilec]) => nosilec.trim() !== '')
    .map(([vir, nosilec]) => ({ vir, nosilec: nosilec.trim() }));
  return {
    naziv: o.naziv.trim(),
    nosilci,
    predvidenaAmortizacija: znesekZaApi(o.predvidenaAmortizacija),
    zakljucek: o.zakljucek === '' ? null : o.zakljucek,
    aktiven: o.aktiven,
    zacetnaPoraba: znesekZaApi(o.zacetnaPoraba, 'Začetno stanje porabe'),
    zacetnaPorabaDo: o.zacetnaPorabaDo === '' ? null : o.zacetnaPorabaDo,
  };
}

/**
 * Programi, za katere obrazec pokaze polje nosilca: programi podjetja in
 * programi, za katere ima projekt nosilec iz casa, ko je prehod se veljal
 * (5.2). Tako se tak nosilec vidi in se ga da odstraniti.
 */
function programiObrazca(programi: string[], o: Obrazec): string[] {
  return [...new Set([...programi, ...Object.keys(o.nosilci).filter((vir) => (o.nosilci[vir] ?? '').trim() !== '')])];
}

function vObrazec(p: Projekt): Obrazec {
  return {
    id: p.id,
    naziv: p.naziv,
    nosilci: Object.fromEntries(p.nosilci.map((n) => [n.vir, n.nosilec])),
    predvidenaAmortizacija: p.predvidenaAmortizacija === null ? '' : znesekZValuto(p.predvidenaAmortizacija).replace(' €', ''),
    zakljucek: p.zakljucek ?? '',
    aktiven: p.aktiven,
    zacetnaPoraba: p.zacetnaPoraba === null ? '' : znesekZValuto(p.zacetnaPoraba).replace(' €', ''),
    zacetnaPorabaDo: p.zacetnaPorabaDo ?? '',
  };
}

export function Projekti({ podjetje }: Lastnosti): JSX.Element {
  const [pregled, setPregled] = useState<PregledProjektov | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [obrazec, setObrazec] = useState<Obrazec | null>(null);
  const [tece, setTece] = useState(false);
  const [najvec, setNajvec] = useState('');

  useEffect(() => {
    let veljaven = true;
    api
      .projekti(podjetje)
      .then((p) => {
        if (!veljaven) return;
        setPregled(p);
        setNajvec(String(p.nastavitve.najvecProjektovNaSredstvo));
      })
      .catch((e: unknown) => {
        if (veljaven) setNapaka(e instanceof Error ? e.message : 'Projektov ni bilo mogoče prebrati.');
      });
    return () => {
      veljaven = false;
    };
  }, [podjetje]);

  const shrani = (d: FormEvent): void => {
    d.preventDefault();
    if (obrazec === null || pregled === null) return;
    let vnos: VnosProjekta;
    try {
      vnos = vVnos(obrazec);
    } catch (e) {
      setNapaka(e instanceof Error ? e.message : 'Neveljaven vnos.');
      return;
    }
    setTece(true);
    setNapaka(null);
    const zahteva = obrazec.id === null ? api.dodajProjekt(podjetje, vnos) : api.spremeniProjekt(podjetje, obrazec.id, vnos);
    zahteva
      .then((shranjen) => {
        setPregled({
          ...pregled,
          projekti:
            obrazec.id === null
              ? [...pregled.projekti, shranjen]
              : pregled.projekti.map((p) => (p.id === shranjen.id ? shranjen : p)),
        });
        setObrazec(null);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Projekta ni bilo mogoče shraniti.');
      })
      .finally(() => {
        setTece(false);
      });
  };

  const odstrani = (p: Projekt): void => {
    if (pregled === null) return;
    if (!window.confirm(`Odstranim projekt »${p.naziv}«?`)) return;
    setNapaka(null);
    api
      .odstraniProjekt(podjetje, p.id)
      .then(() => {
        setPregled({ ...pregled, projekti: pregled.projekti.filter((x) => x.id !== p.id) });
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Projekta ni bilo mogoče odstraniti.');
      });
  };

  const shraniNastavitve = (): void => {
    if (pregled === null) return;
    const vrednost = Number.parseInt(najvec, 10);
    setNapaka(null);
    api
      .shraniNastavitveProjektov(podjetje, { najvecProjektovNaSredstvo: vrednost } satisfies NastavitveProjektov)
      .then((n) => {
        setPregled({ ...pregled, nastavitve: n });
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Nastavitve ni bilo mogoče shraniti.');
      });
  };

  const programi = pregled?.programi ?? [];

  return (
    <section className="kartica" aria-labelledby="projekti">
      <div className="glava-kartice">
        <h2 id="projekti">Projekti</h2>
        {obrazec === null && (
          <button
            type="button"
            className="gumb-dodaj"
            onClick={() => {
              setObrazec({ ...PRAZEN, nosilci: Object.fromEntries(programi.map((v) => [v, ''])) });
              setNapaka(null);
            }}
          >
            <span className="plus" aria-hidden="true" />
            Nov projekt
          </button>
        )}
      </div>
      <p className="podnaslov">
        Šifrant projektov (razpisov) za delitev amortizacije. Knjižbe se projektu pripišejo po stroškovnem
        nosilcu iz programa, ne po imenu. Predvideno amortizacijo (razpoložljiva sredstva) in zaključek vnese
        računovodja; odstotke po sredstvih bo nastavljala stranka.
      </p>

      {pregled !== null && pregled.projekti.length > 0 && (
        <div className="tabela-ovoj">
          <table>
            <thead>
              <tr>
                <th>projekt</th>
                <th>stroškovni nosilec</th>
                <th className="stevilka">predvidena amortizacija</th>
                <th>zaključek</th>
                <th>stanje</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pregled.projekti.map((p) => (
                <tr key={p.id}>
                  <td>{p.naziv}</td>
                  <td>
                    {p.nosilci.length === 0 ? (
                      <span className="drobno">ni vnesen</span>
                    ) : (
                      p.nosilci.map((n) => (
                        <span key={n.vir} className="drobno" style={{ marginRight: '0.6rem' }}>
                          {nazivVira(n.vir) ?? n.vir}: <code>{n.nosilec}</code>
                        </span>
                      ))
                    )}
                  </td>
                  <td className="stevilka">{p.predvidenaAmortizacija === null ? '—' : znesekZValuto(p.predvidenaAmortizacija)}</td>
                  <td>{p.zakljucek === null ? '—' : datum(p.zakljucek)}</td>
                  <td>{p.aktiven ? 'odprt' : 'zaključen'}</td>
                  <td>
                    <button
                      type="button"
                      className="gumb-besedilo"
                      onClick={() => {
                        setObrazec({ ...vObrazec(p), nosilci: { ...Object.fromEntries(programi.map((v) => [v, ''])), ...vObrazec(p).nosilci } });
                        setNapaka(null);
                      }}
                    >
                      uredi
                    </button>
                    {' · '}
                    <button
                      type="button"
                      className="gumb-besedilo"
                      onClick={() => {
                        odstrani(p);
                      }}
                    >
                      odstrani
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pregled !== null && pregled.projekti.length === 0 && obrazec === null && (
        <p className="drobno">Podjetje še nima projektov.</p>
      )}

      {obrazec !== null && (
        <form className="obrazec obrazec-projekta" onSubmit={shrani}>
          <label>
            Naziv projekta
            <input
              type="text"
              value={obrazec.naziv}
              onChange={(d) => {
                setObrazec({ ...obrazec, naziv: d.target.value });
              }}
              required
              maxLength={200}
            />
          </label>
          {programiObrazca(programi, obrazec).map((vir) => (
            <label key={vir}>
              Stroškovni nosilec ({nazivVira(vir) ?? vir})
              {!programi.includes(vir) && <span className="drobno"> – podjetje iz tega programa ne uvaža več; izprazni, da nosilec odstraniš</span>}
              <input
                type="text"
                value={obrazec.nosilci[vir] ?? ''}
                onChange={(d) => {
                  setObrazec({ ...obrazec, nosilci: { ...obrazec.nosilci, [vir]: d.target.value } });
                }}
                maxLength={100}
              />
            </label>
          ))}
          <label>
            Predvidena amortizacija (€)
            <input
              type="text"
              inputMode="decimal"
              placeholder="npr. 120.000,00"
              value={obrazec.predvidenaAmortizacija}
              onChange={(d) => {
                setObrazec({ ...obrazec, predvidenaAmortizacija: d.target.value });
              }}
            />
          </label>
          <label>
            Zaključek projekta
            <input
              type="date"
              value={obrazec.zakljucek}
              onChange={(d) => {
                setObrazec({ ...obrazec, zakljucek: d.target.value });
              }}
            />
          </label>
          <label>
            Začetno stanje porabe (€)
            <span className="drobno"> – amortizacija, pripisana projektu pred portalom</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="npr. 15.000,00"
              value={obrazec.zacetnaPoraba}
              onChange={(d) => {
                setObrazec({ ...obrazec, zacetnaPoraba: d.target.value });
              }}
            />
          </label>
          <label>
            Začetno stanje velja do vključno meseca
            <input
              type="month"
              value={obrazec.zacetnaPorabaDo}
              onChange={(d) => {
                setObrazec({ ...obrazec, zacetnaPorabaDo: d.target.value });
              }}
            />
          </label>
          {obrazec.id !== null && (
            <label className="vrstica">
              <input
                type="checkbox"
                checked={obrazec.aktiven}
                onChange={(d) => {
                  setObrazec({ ...obrazec, aktiven: d.target.checked });
                }}
              />{' '}
              projekt je odprt (sprejema nove odstotke)
            </label>
          )}
          <div className="gumbi-nastavitev">
            <button type="submit" className="gumb" disabled={tece || obrazec.naziv.trim() === ''}>
              {obrazec.id === null ? 'Dodaj projekt' : 'Shrani projekt'}
            </button>
            <button
              type="button"
              onClick={() => {
                setObrazec(null);
                setNapaka(null);
              }}
            >
              Prekliči
            </button>
          </div>
        </form>
      )}

      {pregled !== null && (
        <div className="vrstica" style={{ marginTop: '0.75rem' }}>
          <label className="drobno">
            Največ projektov na enem sredstvu{' '}
            <input
              type="number"
              min={1}
              max={20}
              value={najvec}
              aria-label="Največ projektov na sredstvu"
              onChange={(d) => {
                setNajvec(d.target.value);
              }}
              style={{ width: '4rem' }}
            />
          </label>
          <button
            type="button"
            className="gumb-besedilo"
            disabled={najvec === String(pregled.nastavitve.najvecProjektovNaSredstvo)}
            onClick={shraniNastavitve}
          >
            shrani
          </button>
        </div>
      )}

      {napaka !== null && (
        <p className="napaka" role="alert">
          {napaka}
        </p>
      )}
    </section>
  );
}
